import { useState } from "react"
import {CategoriesKeysLowerCase} from "../Types/ProductCategoriesEnum";
import { IOrganizationReviewForm } from "../Types/Reviews";
import { API_BASE_URL } from "../utils/apiConfig";

interface RequestOptions extends RequestInit {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    body?: any;
    headers?: Record<string, string>;
    /** Если true — пробрасывает ошибку вызывающему коду (нужно для форм) */
    throwOnError?: boolean;
}

export class ApiError extends Error {
    constructor(
        message: string,
        public status: number,
        public data?: any
    ) {
        super(message)
        this.name = 'ApiError'
    }
}

const normalizeErrorMessage = (message: unknown, fallback: string): string => {
    if (Array.isArray(message)) {
        return message.join(', ')
    }
    if (typeof message === 'string' && message.trim()) {
        return message
    }
    return fallback
}

const useApi = <T=any>() => {
    const [data, setData] = useState<T>()
    const [error, setError] = useState<ApiError | null>()
    const [loading, setLoading] = useState<boolean>(false)

    const fetchData = async (url: string, options: RequestOptions ) => {
        setError(null)
        setLoading(true)
        const { throwOnError = false, method = 'GET', headers = {}, ...rest } = options

        try {
                const response = await fetch(url, {
                method,
                headers: {
                    ...headers
                },
                ...rest,
                body: options?.body && ['POST', 'PUT', 'PATCH'].includes(method) ? options.body : undefined
            })

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}))
                throw new ApiError(
                    normalizeErrorMessage(
                        errData.message,
                        `HTTP Error ${response.status}`,
                    ),
                    response.status,
                    errData
                )
            }
            
            const responseData = await response.json()
            setData(responseData)
            return responseData
        } catch (err) {
            const apiError =
                err instanceof ApiError
                    ? err
                    : new ApiError(
                          err instanceof Error ? err.message : 'Network Error',
                          0,
                          err,
                      )
            setError(apiError)
            if (throwOnError) {
                throw apiError
            }
            return undefined
        } finally {
            setLoading(false)
        }
    }

    const removeReview = async (id: string) => await fetchData(`${API_BASE_URL}/reviews/${id}`, {method: 'DELETE', throwOnError: true})
    const getReviewsByType = async (param: 'product' | 'organization') => await fetchData(`${API_BASE_URL}/reviews/${param}`, {method: 'GET'})
    const getAllProducts = async () => await fetchData(`${API_BASE_URL}/product`, {method: 'GET'})
    const getProductsByCategory = async (category: string) => await fetchData(`${API_BASE_URL}/product?category=${category}`, {method: 'GET'})
    const createProduct = async (product: any) => await fetchData(`${API_BASE_URL}/product`, {method: 'POST', body: product, throwOnError: true})
    const deleteProductById = async (id: string) => await fetchData(`${API_BASE_URL}/product/${id}`, {method: 'DELETE', throwOnError: true})
    const updateProduct = async (id: string, body: any) => await fetchData(`${API_BASE_URL}/product/${id}`, {method: 'PATCH', body, throwOnError: true})
    const createOrder = async (orderData: string) => await fetchData(`${API_BASE_URL}/order`, {method: 'POST', body: orderData, headers: {'Content-Type': 'application/json'}, throwOnError: true})
    const createProductReview = async (reviewData: FormData) => await fetchData(`${API_BASE_URL}/reviews/product`, {method: 'POST', body: reviewData, throwOnError: true})
    

    const createOrgReview = async (reviewData: IOrganizationReviewForm) => {
        const jsonData = JSON.stringify(reviewData)
        return await fetchData(`${API_BASE_URL}/reviews/organization`, {method: 'POST', body: jsonData, headers: {'Content-Type': 'application/json'}, throwOnError: true})
    }
    const getCategoryPageContent = async (category: CategoriesKeysLowerCase, page: number = 1, popularitySortOrder?: 'desc' | 'asc' | null, searchWord?:string, limit:number = 6) => {
        const urlParams = new URLSearchParams();
        urlParams.append('category', category.toUpperCase())
        urlParams.append('limit', limit.toString())
        urlParams.append('page', page.toString())
        if (popularitySortOrder) {
            urlParams.append('sortBy','popularityScore')
            urlParams.append('order', popularitySortOrder)
        }

        if (searchWord) {
            urlParams.append('search', searchWord)
        }

        return await fetchData(`${API_BASE_URL}/product?${urlParams.toString()}`, {method: 'GET'})
    }

    const getPopularProducts = async (popularitySortOrder: 'desc' | 'asc') => {
        const urlParams = new URLSearchParams();
        if (popularitySortOrder) {
            urlParams.append('sortBy','popularityScore')
            urlParams.append('order', popularitySortOrder)
            urlParams.append('limit', '40')
        }

        return await fetchData(`${API_BASE_URL}/product?${urlParams.toString()}`, {method: 'GET'})
    }

    const getRecentProducts = async (sortOrder: 'desc' | 'asc', limit = 40) => {
        const urlParams = new URLSearchParams();
        if (sortOrder) {
            urlParams.append('sortBy','created_at')
            urlParams.append('order', sortOrder)
            urlParams.append('limit', `${limit}`)
        }

        return await fetchData(`${API_BASE_URL}/product?${urlParams.toString()}`, {method: 'GET'})
    }

    const getOrders = async (type: 'consultation' | 'order', status: 'active' | 'completed' | 'canceled') => {
        const urlParams = new URLSearchParams();
        urlParams.append('type', type.toUpperCase())
        urlParams.append('status', status.toUpperCase())

        return await fetchData(`${API_BASE_URL}/order?${urlParams.toString()}`, {method: 'GET'})
    }

    const getReviews = async (page: number = 1, type?: 'organization' | 'product', sortEntity?: 'username' | 'rating' | 'created_at', sortOrder?: 'desc' | 'asc' | null, searchWord?:string, limit:number = 3) => {
        const urlParams = new URLSearchParams();
        urlParams.append('limit', limit.toString())
        urlParams.append('page', page.toString())
        if (sortOrder) {
            urlParams.append('order', sortOrder)
        }

        if (sortEntity) {
            urlParams.append('sortBy', sortEntity)
        }

        if (searchWord) {
            urlParams.append('search', searchWord)
        }

        if(type) {
            urlParams.append('type', type)
        }

        return await fetchData(`${API_BASE_URL}/reviews?${urlParams.toString()}`, {method: 'GET'})
    }

    const changeOrderStatus = async (id: string, status: 'active' | 'canceled' | 'completed') => await fetchData(`${API_BASE_URL}/order/${id}`, {method: 'PUT', body: JSON.stringify({status: status.toUpperCase()}), headers: {'Content-Type': 'application/json'}, throwOnError: true})
    const getProductsById = async (id: string) => await fetchData(`${API_BASE_URL}/product/${id}`, {method: 'GET'})
    const getProductReview = async (id: string) => await fetchData(`${API_BASE_URL}/reviews/product/${id}`, {method: 'GET'})
    return {
        data,
        error,
        loading,
        getReviews,
        getAllProducts,
        getProductsByCategory,
        createProduct,
        createOrder,
        deleteProudctById: deleteProductById,
        deleteProductById,
        updateProduct,
        getCategoryPageContent,
        createOrgReview,
        getReviewsByType,
        removeReview,
        getPopularProducts,
        getRecentProducts,
        getOrders,
        changeOrderStatus,
        getProductsById,
        createProductReview,
        getProductReview
    }
    
}

export default useApi
